import { useMutation, useQueries, useQueryClient } from '@tanstack/react-query'
import { ContentScopeType, type ContentTextDTO, Permission } from '@today/types'
import type { TextHeaderViewProps, UsageLinkView } from '@today/ui-domain'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { productKey } from '@/hooks/products/useProductEditor'
import { contentService } from '@/services/content.service'
import { productService } from '@/services/product.service'
import { textHeaderBody, textHeaderErrors } from './text-form'
import { contentKeys, useAllAssignments, useContentT, useContentViewer } from './useContentQueries'

/** "Used by" links: tenant defaults, then each assigned product (named when the viewer may read products). */
function useUsageLinks(textId: string): UsageLinkView[] {
	const { t } = useContentT()
	const { permissions } = useContentViewer()
	const assignments = useAllAssignments()
	const own = (assignments.data ?? []).filter((a) => a.textId === textId)
	const productIds = [
		...new Set(own.flatMap((a) => (a.scopeType === ContentScopeType.PRODUCT && a.scopeId ? [a.scopeId] : []))),
	]
	const canReadProducts = permissions.includes(Permission.PRODUCT_READ)
	const products = useQueries({
		queries: productIds.map((id) => ({
			queryKey: productKey(id),
			queryFn: () => productService.get(id),
			enabled: canReadProducts,
			staleTime: 30_000,
		})),
	})
	const links: UsageLinkView[] = own.some((a) => a.scopeType === ContentScopeType.TENANT)
		? [{ label: t('header.tenantDefaults'), href: '/content/tenant-defaults' }]
		: []
	productIds.forEach((id, i) => {
		links.push({ label: products[i]?.data?.name ?? id, href: `/products/${id}?tab=legal-texts` })
	})
	return links
}

/** The editor header (TO-420 content-admin-ui REQ-003): read-only code, PATCH of name/description only. */
export function useTextHeader(text: ContentTextDTO, typeName: string): TextHeaderViewProps {
	const { t, fieldError } = useContentT()
	const navigate = useNavigate()
	const client = useQueryClient()
	const { caps } = useContentViewer()
	const [name, setName] = useState(text.name)
	const [description, setDescription] = useState(text.description ?? '')
	const [errors, setErrors] = useState<Record<string, string>>({})
	const [formError, setFormError] = useState<string | null>(null)
	const save = useMutation({
		mutationFn: (body: Record<string, unknown>) => contentService.updateText(text.id, body),
		onSuccess: async (updated) => {
			client.setQueryData(contentKeys.text(text.id), updated)
			await client.invalidateQueries({ queryKey: contentKeys.texts })
		},
		onError: (error) => {
			const { field, message } = fieldError(error)
			if (field === 'form') setFormError(message)
			else setErrors({ [field]: message })
		},
	})
	return {
		t,
		code: text.code,
		typeName,
		sourceLocale: text.sourceLocale,
		name,
		description,
		missingLocales: text.missingLocales,
		usageLinks: useUsageLinks(text.id),
		errors,
		formError,
		isBusy: save.isPending,
		onChange: (field, value) => (field === 'name' ? setName(value) : setDescription(value)),
		onNavigate: (href) => void navigate(href),
		onSave: caps.write
			? () => {
					setFormError(null)
					const body = textHeaderBody(name, description)
					const found = textHeaderErrors(body)
					setErrors(Object.fromEntries(Object.entries(found).map(([f, key]) => [f, t(key)])))
					if (Object.keys(found).length === 0) save.mutate(body)
				}
			: undefined,
	}
}
